import React, { useState, useEffect } from 'react';
import { BookOpen, Quote, Sparkles, Loader2 } from 'lucide-react';
import { bibleApi } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const DailyVerse = () => {
  const { lang } = useLanguage();
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [explaining, setExplaining] = useState(false);

  const texts = {
    de: { title: 'Tagesvers', explain: 'KI-Erklaerung', explaining: 'Erklaere...', cta: 'Zum Bibelstudium', ctaSub: 'Moechten Sie mehr ueber Gottes Wort erfahren?' },
    en: { title: 'Daily Verse', explain: 'AI Explanation', explaining: 'Explaining...', cta: 'Bible Study', ctaSub: 'Want to learn more about God\'s Word?' },
    fr: { title: 'Verset du jour', explain: 'Explication IA', explaining: 'Explication...', cta: 'Etude biblique', ctaSub: 'Vous voulez en savoir plus sur la Parole de Dieu?' },
  };
  const t = texts[lang] || texts.de;

  useEffect(() => {
    const fetchVerse = async () => {
      try {
        setLoading(true);
        const data = await bibleApi.getDailyVerse();
        setVerse(data);
        setError(null);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchVerse();
  }, []);

  const explainVerse = async () => {
    if (!verse || explaining) return;
    setExplaining(true);
    setExplanation('');

    try {
      const response = await fetch(`${API_BASE}/ai/explain-verse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verse_text: verse.text,
          reference: verse.reference,
          language: lang,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                setExplanation(prev => prev + data.text);
              }
            } catch (err) {
              console.error('Error parsing verse SSE:', err);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error explaining verse:', err);
      setExplanation('Fehler bei der Erklaerung. Bitte versuche es erneut.');
    } finally {
      setExplaining(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="animate-pulse">Lade Tagesvers...</div>
        </div>
      </section>
    );
  }

  if (error || !verse) return null;

  const { reference, text, translation, date: verseDate } = verse;

  return (
    <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <BookOpen className="w-8 h-8 text-blue-300" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">{t.title}</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
            <Quote className="w-12 h-12 text-blue-300 mb-6" />
            <blockquote className="text-2xl md:text-3xl text-white font-light leading-relaxed mb-8" data-testid="daily-verse-text">
              {text}
            </blockquote>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-6 border-t border-white/20">
              <div>
                <div className="text-blue-200 font-semibold text-lg">{reference}</div>
                <div className="text-white/60 text-sm mt-1">{translation}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white/60 text-sm">{verseDate}</span>
                <button
                  onClick={explainVerse}
                  disabled={explaining}
                  data-testid="verse-explain-btn"
                  className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-lg text-sm font-medium transition-all inline-flex items-center gap-2 border border-white/20 disabled:opacity-50"
                >
                  {explaining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {explaining ? t.explaining : t.explain}
                </button>
              </div>
            </div>

            {/* AI Explanation */}
            {explanation && (
              <div className="mt-6 p-5 bg-white/10 rounded-2xl border border-white/15" data-testid="verse-explanation">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span className="text-amber-200 text-sm font-semibold">KI-Erklaerung</span>
                </div>
                <p className="text-white/90 leading-relaxed text-sm">{explanation}</p>
              </div>
            )}
          </div>

          <div className="text-center mt-8">
            <p className="text-white/80 mb-4">{t.ctaSub}</p>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-3 bg-white text-blue-900 rounded-lg font-semibold hover:bg-blue-50 transition-all hover:scale-105 inline-flex items-center gap-2"
            >
              <BookOpen className="w-5 h-5" />
              {t.cta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyVerse;
